import {
  DEFAULT_SCI_MESSAGE,
  SCI_ATTACHMENT_CONTENT_TYPES,
  SCI_ATTACHMENT_MAX_SIZE_BYTES,
  SCI_REPORT_TYPES,
  isSCIEligibleExistingAttachment,
  sciAttachmentUploadFileSchema,
  type ClaimAttachmentSummary,
  type SCIReportType,
  type SCIAttachmentUploadTarget,
} from '@billing/validation/attachment';
import { isSuccessfulManitobaClaimQuerySubmission } from '@billing/validation/claimQuery';
import {
  Alert,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  UnstyledButton,
  useComputedColorScheme,
} from '@mantine/core';
import { Dropzone, type FileWithPath } from '@mantine/dropzone';
import { useNotification, useTranslation } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconCheck,
  IconFile,
  IconSend,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import {
  claimAttachmentQueryKeys,
  getAttachmentDownloadURL,
  useGetClaimAttachments,
  usePrepareSCIClaimAttachment,
  useSubmitSCIClaimQuery,
} from 'api/resource/claimAttachment';
import { claimQueryKeys } from 'api/resource/claim';
import type { Claim } from 'interfaces';
import { useEffect, useState } from 'react';
import { uploadFileToSignedURL } from 'utils/fileUpload';
import { buttonTextColor } from 'utils/buttonUtils';
import { getFileAppearance, getFilenameExtension } from 'utils/fileUtils';

interface SupportingDocumentUploadModalProps {
  claim: Claim | null;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const reportTypeTranslationKeys: Record<SCIReportType, string> = {
  'Operative/Procedure Report': 'operativeProcedure',
  'Pathology Report': 'pathology',
  'Consultation Notes': 'consultation',
  'Chart Notes': 'chart',
  Other: 'other',
};

const ExistingAttachmentCard = ({
  attachment,
  disabled,
  selected,
  onSelect,
}: {
  attachment: ClaimAttachmentSummary;
  disabled: boolean;
  selected: boolean;
  onSelect: () => void;
}) => {
  const { translate } = useTranslation();
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const extension = getFilenameExtension(attachment.originalFilename);
  const appearance = getFileAppearance(attachment.originalFilename);
  const canPreviewImage = extension === 'jpg' || extension === 'jpeg';
  const canPreviewPDF = extension === 'pdf';

  useEffect(() => {
    let active = true;
    let objectURL: string | null = null;
    if (!canPreviewImage && !canPreviewPDF) {
      setPreviewURL(null);
      setPreviewLoading(false);
      return () => {
        active = false;
      };
    }
    setPreviewLoading(true);
    void getAttachmentDownloadURL(attachment.id)
      .then((signedURL) => fetch(signedURL))
      .then((response) => {
        if (!response.ok) throw new Error('AttachmentPreviewDownloadError');
        return response.blob();
      })
      .then((blob) => {
        const previewBlob = canPreviewPDF
          ? new Blob([blob], { type: 'application/pdf' })
          : blob;
        objectURL = URL.createObjectURL(previewBlob);
        if (active) {
          setPreviewURL(objectURL);
        } else {
          URL.revokeObjectURL(objectURL);
          objectURL = null;
        }
      })
      .catch(() => {
        if (active) setPreviewURL(null);
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });
    return () => {
      active = false;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [attachment.id, canPreviewImage, canPreviewPDF]);

  const selectionLabel = selected
    ? translate(
        'pages:claims.supportingDocument.selectedExistingAttachment',
        { filename: attachment.originalFilename },
        `Selected: ${attachment.originalFilename}`
      )
    : translate(
        'pages:claims.supportingDocument.selectExistingAttachment',
        { filename: attachment.originalFilename },
        `Select ${attachment.originalFilename}`
      );

  return (
    <Paper
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        borderRadius: 'var(--mantine-radius-md)',
        border: selected
          ? '2px solid var(--mantine-color-blue-6)'
          : '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        background:
          'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
        boxShadow: selected
          ? '0 0 0 2px light-dark(var(--mantine-color-blue-1), var(--mantine-color-blue-9))'
          : 'var(--mantine-shadow-xs)',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <UnstyledButton
        type="button"
        aria-label={selectionLabel}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onSelect}
        pos="absolute"
        inset={0}
        style={{ zIndex: 1 }}
      />
      <Box
        h={140}
        pos="relative"
        style={{
          overflow: 'hidden',
          background:
            'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))',
          borderBottom:
            '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))',
        }}
      >
        {previewLoading && (
          <Group justify="center" h="100%">
            <Loader size="sm" />
          </Group>
        )}
        {!previewLoading && canPreviewImage && previewURL && (
          <Image src={previewURL} alt="" w="100%" h="100%" fit="contain" />
        )}
        {!previewLoading && canPreviewPDF && previewURL && (
          <Box
            component="iframe"
            src={`${previewURL}#page=1&toolbar=0&navpanes=0&scrollbar=0`}
            title={translate(
              'pages:claims.supportingDocument.attachmentPreview',
              { filename: attachment.originalFilename },
              `${attachment.originalFilename} preview`
            )}
            w="100%"
            h="100%"
            style={{ border: 0, pointerEvents: 'none' }}
          />
        )}
        {!previewLoading &&
          (!previewURL || (!canPreviewImage && !canPreviewPDF)) && (
            <Group justify="center" h="100%">
              <ThemeIcon
                size={72}
                radius="md"
                variant="light"
                color={appearance.color}
              >
                {appearance.icon}
              </ThemeIcon>
            </Group>
          )}
        {selected && (
          <ThemeIcon
            pos="absolute"
            top={8}
            right={8}
            radius="xl"
            color="blue"
            aria-hidden
            style={{ zIndex: 2, pointerEvents: 'none' }}
          >
            <IconCheck size={16} />
          </ThemeIcon>
        )}
      </Box>
      <Box p="sm" ta="left">
        <Text
          size="sm"
          fw={600}
          mih={40}
          lineClamp={2}
          title={attachment.originalFilename}
        >
          {attachment.originalFilename}
        </Text>
        <Text size="xs" c="dimmed" mt={3} tt="uppercase">
          {extension}
        </Text>
      </Box>
    </Paper>
  );
};

export const SupportingDocumentUploadModal = ({
  claim,
  open,
  setOpen,
}: SupportingDocumentUploadModalProps) => {
  const { translate } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const { open: notify } = useNotification();
  const queryClient = useQueryClient();
  const prepareUpload = usePrepareSCIClaimAttachment();
  const submitQuery = useSubmitSCIClaimQuery();
  const attachments = useGetClaimAttachments({
    claimID: claim?.id,
    enabled: open,
    sciEligible: true,
  });
  const [file, setFile] = useState<FileWithPath | null>(null);
  const [reportType, setReportType] = useState<SCIReportType | null>(null);
  const [message, setMessage] = useState(DEFAULT_SCI_MESSAGE);
  const [uploadTarget, setUploadTarget] =
    useState<SCIAttachmentUploadTarget | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttachmentID, setSelectedAttachmentID] = useState<
    string | null
  >(null);
  const eligibleExistingAttachments = (attachments.data ?? []).filter(
    isSCIEligibleExistingAttachment
  );
  const selectedExistingAttachment = eligibleExistingAttachments.find(
    (attachment) => attachment.id === selectedAttachmentID
  );
  const isSubmitting =
    prepareUpload.isPending || isUploading || submitQuery.isPending;
  const isSubmitDisabled =
    !reportType || !message.trim() || (!file && !selectedExistingAttachment);

  const reset = () => {
    setFile(null);
    setReportType(null);
    setMessage(DEFAULT_SCI_MESSAGE);
    setUploadTarget(null);
    setUploadProgress(0);
    setError(null);
    setSelectedAttachmentID(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    setOpen(false);
  };

  const handleDrop = (files: FileWithPath[]) => {
    const selectedFile = files[0];
    if (!selectedFile) return;
    const parsed = sciAttachmentUploadFileSchema.safeParse({
      originalFilename: selectedFile.name,
      contentType: selectedFile.type,
      sizeBytes: selectedFile.size,
    });
    if (!parsed.success) {
      setError(
        translate(
          'pages:claims.supportingDocument.invalidFile',
          'Choose a DOC, DOCX, PDF, TXT, JPG, or JPEG file up to 5 MB with a filename no longer than 60 characters.'
        )
      );
      return;
    }
    setFile(selectedFile);
    setSelectedAttachmentID(null);
    setUploadTarget(null);
    setUploadProgress(0);
    setError(null);
  };

  const handleSuccessfulSubmission = async (claimID: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: claimAttachmentQueryKeys.byClaim(claimID),
      }),
      queryClient.invalidateQueries({
        queryKey: claimQueryKeys.getClaimHistory(claimID),
      }),
      queryClient.invalidateQueries({ queryKey: claimQueryKeys.all }),
    ]);
    notify?.({
      type: 'success',
      message: translate(
        'pages:claims.supportingDocument.successTitle',
        'Supporting document sent'
      ),
      description: translate(
        'pages:claims.supportingDocument.successDescription',
        'The document was submitted to Manitoba Health.'
      ),
    });
    reset();
    setOpen(false);
  };

  const assertSuccessfulResponse = (
    response: Awaited<ReturnType<typeof submitQuery.mutateAsync>>
  ) => {
    if (isSuccessfulManitobaClaimQuerySubmission(response)) return;
    throw new Error(
      typeof response.response === 'string'
        ? response.response
        : response.message || 'Manitoba Health rejected the query.'
    );
  };

  const handleSubmit = async () => {
    if (!claim?.id || !reportType || !message.trim()) return;
    if (!file && !selectedExistingAttachment) return;

    setError(null);
    try {
      if (selectedExistingAttachment) {
        const response = await submitQuery.mutateAsync({
          claimID: claim.id,
          attachmentID: selectedExistingAttachment.id,
          reportType,
          message: message.trim(),
        });
        assertSuccessfulResponse(response);
        await handleSuccessfulSubmission(claim.id);
        return;
      }

      const fileMetadata = sciAttachmentUploadFileSchema.parse({
        originalFilename: file!.name,
        contentType: file!.type,
        sizeBytes: file!.size,
      });
      const target =
        uploadTarget ??
        (await prepareUpload.mutateAsync({
          claimID: claim.id,
          file: fileMetadata,
        }));
      setUploadTarget(target);
      if (!uploadTarget) {
        setIsUploading(true);
        try {
          await uploadFileToSignedURL({
            signedURL: target.uploadURL,
            data: file!,
            contentType: target.contentType,
            onProgress: setUploadProgress,
          });
        } finally {
          setIsUploading(false);
        }
      }

      const response = await submitQuery.mutateAsync({
        claimID: claim.id,
        attachment: {
          id: target.id,
          originalFilename: target.originalFilename,
          contentType: target.contentType,
          sizeBytes: target.sizeBytes,
        },
        reportType,
        message: message.trim(),
      });
      assertSuccessfulResponse(response);
      await handleSuccessfulSubmission(claim.id);
    } catch (submissionError) {
      await queryClient.invalidateQueries({
        queryKey: claimAttachmentQueryKeys.byClaim(claim.id),
      });
      setUploadTarget(null);
      setUploadProgress(0);
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : translate(
              'pages:claims.supportingDocument.submitError',
              'The document could not be submitted to Manitoba Health.'
            )
      );
    }
  };

  const invalidFileMessage = translate(
    'pages:claims.supportingDocument.invalidFile',
    'Choose a DOC, DOCX, PDF, TXT, JPG, or JPEG file up to 5 MB with a filename no longer than 60 characters.'
  );

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      title={translate(
        'pages:claims.supportingDocument.title',
        'Upload Supporting Document'
      )}
      centered
      size="xl"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <Stack gap="md">
        {(attachments.isLoading || eligibleExistingAttachments.length > 0) && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {translate(
                'pages:claims.supportingDocument.existingAttachment',
                'Existing claim attachment'
              )}
            </Text>
            {attachments.isLoading ? (
              <Group justify="center" mih={120}>
                <Loader size="sm" />
              </Group>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                {eligibleExistingAttachments.map((attachment) => (
                  <ExistingAttachmentCard
                    key={attachment.id}
                    attachment={attachment}
                    selected={attachment.id === selectedAttachmentID}
                    disabled={isSubmitting}
                    onSelect={() => {
                      const nextAttachmentID =
                        attachment.id === selectedAttachmentID
                          ? null
                          : attachment.id;
                      setSelectedAttachmentID(nextAttachmentID);
                      if (nextAttachmentID) {
                        setFile(null);
                        setUploadTarget(null);
                        setUploadProgress(0);
                      }
                    }}
                  />
                ))}
              </SimpleGrid>
            )}
          </Stack>
        )}

        {!selectedExistingAttachment && (
          <>
            {eligibleExistingAttachments.length > 0 && (
              <Text size="sm" c="dimmed" ta="center">
                {translate(
                  'pages:claims.supportingDocument.orUpload',
                  'Or upload a new document'
                )}
              </Text>
            )}
            <Dropzone
              accept={[...SCI_ATTACHMENT_CONTENT_TYPES]}
              maxSize={SCI_ATTACHMENT_MAX_SIZE_BYTES}
              maxFiles={1}
              multiple={false}
              disabled={isSubmitting}
              onDrop={handleDrop}
              onReject={() => setError(invalidFileMessage)}
              data-testid="supporting-document-dropzone"
            >
              <Group justify="center" gap="sm" mih={100}>
                <Dropzone.Accept>
                  <IconUpload size={28} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={28} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFile size={28} />
                </Dropzone.Idle>
                <Text ta="center">
                  {translate(
                    'pages:claims.supportingDocument.dropzone',
                    'Drop one DOC, DOCX, PDF, TXT, JPG, or JPEG file here, or click to choose it.'
                  )}
                </Text>
              </Group>
            </Dropzone>

            {file && (
              <Paper withBorder p="sm" radius="md">
                <Group justify="space-between" wrap="nowrap">
                  <Text truncate>{file.name}</Text>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    disabled={isSubmitting}
                    onClick={() => {
                      setFile(null);
                      setUploadTarget(null);
                      setUploadProgress(0);
                    }}
                  >
                    {translate('common:buttons.remove', 'Remove')}
                  </Button>
                </Group>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <Progress value={uploadProgress} mt="xs" />
                )}
              </Paper>
            )}
          </>
        )}

        <Select
          required
          label={translate(
            'pages:claims.supportingDocument.reportType',
            'Report type'
          )}
          placeholder={translate(
            'pages:claims.supportingDocument.reportTypePlaceholder',
            'Select a report type'
          )}
          data={SCI_REPORT_TYPES.map((value) => ({
            value,
            label: translate(
              `pages:claims.supportingDocument.reportTypes.${reportTypeTranslationKeys[value]}`,
              value
            ),
          }))}
          value={reportType}
          onChange={(value) => setReportType(value as SCIReportType | null)}
          disabled={isSubmitting}
        />
        <Textarea
          required
          maxLength={250}
          label={translate(
            'pages:claims.supportingDocument.message',
            'Message to Manitoba Health'
          )}
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
          disabled={isSubmitting}
          autosize
          minRows={2}
        />

        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        )}

        <Divider />
        <Flex w="100%" justify="center" gap="lg">
          <Button
            variant="outline"
            color="gray"
            c={buttonTextColor(colorScheme, isSubmitting)}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {translate('common:buttons.cancel', 'Cancel')}
          </Button>
          <Button
            variant="outline"
            color="green"
            c={buttonTextColor(colorScheme, isSubmitting || isSubmitDisabled)}
            leftSection={<IconSend size={16} />}
            onClick={() => void handleSubmit()}
            loading={isSubmitting}
            disabled={isSubmitDisabled}
          >
            {translate('pages:claims.supportingDocument.submit', 'Submit')}
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
};
